import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import PostHeader from "./Post/PostHeader";
import PostMedia from "./Components/PostMedia/PostMedia";
import "./Styles/Styles.scss";
import PostActions from "./Components/PostActions/PostActions";
import PostContent from "./Post/PostContent";
import PollContent from "./PollContent/PollContent";
import PostContentShare from "./Components/PostContentShare/PostContentShare";
import PostChallenge from "./Components/PostChallenge/PostChallenge";
import Toast from "../components/Toast/Toast";
import { usePost } from "../hooks/usePost";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SkeletonLoader from "./Components/SkeletonLoader/SkeletonLoader";
import { LuFileSpreadsheet } from "react-icons/lu";
import AddPost from "./AddPost/AddPost"
import { FaPlus } from "react-icons/fa";
import { setSelectedCategory } from "../redux/categoriesSlice";
import { clearPosts } from "../redux/postsSlice";
import axios from "axios";
import qs from "qs";

// Memoized Post Item Component to prevent unnecessary rerenders
const PostItem = React.memo(({
    post,
    postId,
    categories,
    selectedCategory,
    currentUser,
    activeMediaIndex,
    onCommentDisable,
    showToast,
    onEditPost,
    onDeletePost,
    onCarousalClick,
    toggleLikePost,
    handlePollVote,
    onCommentClick
}) => {
    // Memoize media keys to prevent recalculation
    const mediaKeys = useMemo(() =>
        post?.media ? Object.keys(post?.media) : [],
        [post?.media]
    );

    return (
        <div className="post">
            <PostHeader
                post={post}
                postId={postId}
                onCommentDisable={onCommentDisable}
                categories={categories}
                selectedCategory={selectedCategory}
                showToast={showToast}
                onEditPost={onEditPost}
                onDeletePost={onDeletePost}
            />

            {mediaKeys?.length > 0 && (
                <PostMedia
                    post={post}
                    postId={postId}
                    activeIndex={activeMediaIndex || 0}
                    onCarousalButtonClick={onCarousalClick}
                />
            )}
            {post?.type === "CONTENT" && (
                <PostContentShare content={post?.data} type={post?.content_type} />
            )}
            {post?.type === "CHALLENGE" && (
                <PostChallenge post={post} />
            )}
            {post?.type === "POLL" && (
                <PollContent
                    poll={post?.data}
                    postId={postId}
                    currentUser={currentUser}
                    onVote={handlePollVote}
                />
            )}
            <div className="post-caption">
                <PostContent content={post?.content} gif={post?.gif_url} />
            </div>
            <PostActions
                comments={Number(post?.comment_count) || 0}
                isLiked={post?.user_has_liked}
                postId={post?.id}
                toggleLikePost={toggleLikePost}
                isCommentsDisabled={post?.comments_disabled}
                likesCount={post?.like_count || 0}
                onCommentClick={onCommentClick}
                vanityUrl={post?.vanity_url || ""}
                post={post}
            />
        </div>
    );
});

const HomePage = ({ handleAddPostClick }) => {
    const dispatch = useDispatch();
    const { categoryId } = useParams();
    const currentUser = useSelector((state) => state.projectInfo.uid);

    const { community_block_post_add } = useSelector((state) => state.projectInfo);

    const selectedCategory = useSelector((state) => state.categories.selectedCategory);
    const categories = useSelector((state) => state.categories.entities);
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const loadingMoreRef = useRef(false);
    const [sortOption, setSortOption] = useState("latest");
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    const [hasMore, setHasMore] = useState(true);
    const fetchingRef = useRef(false);

    const currentPageRef = useRef(1);
    const hasMoreRef = useRef(true);
    const sortOptionRef = useRef(sortOption);
    sortOptionRef.current = sortOption;
    const selectedCategoryRef = useRef(selectedCategory);
    selectedCategoryRef.current = selectedCategory;

    const projectInfo = useSelector((state) => state.projectInfo);

    const {
        posts,
        postOrder,
        loading,
        activeMediaIndices,
        loadInitialPosts,
        loadMorePosts,
        likePost,
        setPostMediaIndex,
        voteOnPoll,
        togglePostComments,
        deletePost,
        updatePost,
    } = usePost();

    const hasNoPosts = useMemo(() =>
        isLoaded && Object.keys(posts)?.length === 0,
        [isLoaded, posts]
    );

    // Restore scroll position on mount
    useEffect(() => {
        const scrollPos = sessionStorage.getItem('ScrollPosition');
        const container = document.querySelector(".community-content");
        if (scrollPos && container) {
            container.scrollTop = scrollPos;
        }
    }, []);

    // Handle category changes (prevent render loop)
    useEffect(() => {
        if (selectedCategory !== categoryId) {
            setIsLoaded(false);
            setHasMore(true);
            currentPageRef.current = 1;
            hasMoreRef.current = true;
            dispatch(setSelectedCategory(categoryId));
            dispatch(clearPosts());
            fetchingRef.current = false;
        }
    }, [categoryId, selectedCategory, dispatch]);

    // Memoized fetch function to prevent recreation on every render
    const fetchDataCallback = useCallback(async () => {
        try {
            // Only fetch if posts are empty and not already fetching
            const postsCount = Object.keys(posts || {}).length;

            if (postsCount === 0 && !fetchingRef.current) {
                fetchingRef.current = true; // Set flag to prevent double fetch

                window.scrollTo(0, 0);
                const container = document.querySelector(".community-content");
                if (container) {
                    container.scrollTop = 0;
                }
                setIsLoaded(false);
                const data = await loadInitialPosts(selectedCategory ?? categoryId, sortOption);
                const loadedPosts = Object.keys(data.posts || {}).map(key => data.posts[key]);
                currentPageRef.current = 1;
                if (loadedPosts.length) {
                    hasMoreRef.current = true;
                    setHasMore(true);
                    fetchingRef.current = false;
                } else {
                    hasMoreRef.current = false;
                    setHasMore(false);
                }
                setIsLoaded(true);
            } else if (Object.keys(posts || {}).length === 1) {
                window.scrollTo(0, 1);
            }
        } catch (error) {
            console.error("Error loading posts", error);
            setIsLoaded(true);
            fetchingRef.current = false; // Reset flag on error
        }
    }, [posts, selectedCategory, categoryId, sortOption, loadInitialPosts]);

    // Main data fetching effect
    useEffect(() => {
        fetchDataCallback();
    }, [fetchDataCallback]);

    const onCommentClick = useCallback(async (postId) => {
        const container = document.querySelector(".community-content");
        const scrollPosition = container.scrollTop;
        const sessionId = localStorage.getItem('sessionId');
        const { uid, pubid, dev_id, channelid, app_id, accessToken } = projectInfo;
        sessionStorage.setItem('ScrollPosition', scrollPosition);
        let ctimestamp = Date.now().toString();
        let ctime = ctimestamp.slice(0, 10);
        const data = {
            session_id: sessionId,
            user_id: uid,
            device_id: dev_id,
            publisherid: pubid,
            app_id: String(app_id ?? 666),
            channel_id: channelid,
            event_type: "POP14",
            timestamp: ctime,
            post_id: String(postId),
        };
        const customConfig = {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Access-Control-Allow-Origin": true,
                crossorigin: true,
                "access-token": accessToken,
            },
        };
        try {
            await axios.post(
                "https://analytics.poppo.tv/event",
                qs.stringify(data),
                customConfig
            );
        } catch (eventError) {
            console.error("Error sending analytics event:", eventError);
        }
    }, []);

    const loadNextPage = useCallback(async () => {
        if (loadingMoreRef.current || !hasMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        const nextPage = currentPageRef.current + 1;
        try {
            const result = await loadMorePosts(
                nextPage,
                sortOptionRef.current,
                selectedCategoryRef.current
            );
            const count = (result?.postOrder || []).length;
            if (count > 0) {
                currentPageRef.current = nextPage;
                hasMoreRef.current = true;
            } else {
                hasMoreRef.current = false;
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error loading more posts:", error);
        } finally {
            setLoadingMore(false);
            loadingMoreRef.current = false;
        }
    }, [loadMorePosts]);

    const observerRef = useRef(null);
    const sentinelCallbackRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (node) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        loadNextPage();
                    }
                },
                { rootMargin: "300px" }
            );
            observerRef.current.observe(node);
        }
    }, [loadNextPage]);

    const loadNextPageRef = useRef(loadNextPage);
    loadNextPageRef.current = loadNextPage;

    useEffect(() => {
        const checkBottom = () => {
            if (loadingMoreRef.current || !hasMoreRef.current) return;
            const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            const docHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
            if (scrollTop + windowHeight >= docHeight - 300) {
                loadNextPageRef.current();
            }
        };
        window.addEventListener('scroll', checkBottom, { passive: true });
        return () => window.removeEventListener('scroll', checkBottom);
    }, []);

    const onCarousalClick = useCallback((postId, mediaKeys, dir) => {
        const activeIndex = activeMediaIndices[postId] || 0;
        let newIndex;

        if (dir === "prev") {
            newIndex = Math.max(0, activeIndex - 1);
        } else {
            newIndex = Math.min(mediaKeys?.length - 1, activeIndex + 1);
        }

        setPostMediaIndex(postId, newIndex);
    }, [activeMediaIndices, setPostMediaIndex]);

    const onDisableComment = useCallback(async (postId) => {
        if (!posts[postId]) return;
        const currentStatus = posts[postId].comments_disabled;
        await togglePostComments(postId, currentStatus);
    }, [posts, togglePostComments]);

    const toggleLikePost = useCallback(async (postId, isLiked) => {
        await likePost(postId, isLiked);
    }, [likePost]);

    const handlePollVote = useCallback(async (postId, updatedOptions) => {
        await voteOnPoll(postId, updatedOptions);
    }, [voteOnPoll]);

    const handleSortSelection = useCallback((option) => {
        if (option === sortOption) {
            setShowSortDropdown(false);
            setIsLoaded(false);
            return;
        }
        setSortOption(option);
        setShowSortDropdown(false);
        setIsLoaded(false);
        fetchingRef.current = false;
        currentPageRef.current = 1;
        hasMoreRef.current = true;
        dispatch(clearPosts());
    }, [sortOption, dispatch]);

    const showToast = useCallback((message, type = "success") => {
        setToast({ show: true, message, type });
    }, []);

    const hideToast = useCallback(() => {
        setToast(prev => ({ ...prev, show: false }));
    }, []);

    const handleEditPost = useCallback((post, postId) => {
        setEditingPost({ ...post, postId });
        setShowEditPopup(true);
    }, []);

    const handleDeletePost = useCallback(async (postId) => {
        try {
            await deletePost(postId);
            showToast("Post deleted successfully", "success");
            // loadInitialPosts(selectedCategory, sortOption);
            // return Promise.resolve();
        } catch (error) {
            console.error("Error deleting post:", error);
            showToast("Failed to delete post", "error");
            return Promise.reject(error);
        }
    }, [deletePost, showToast]);

    const handleEditSave = useCallback(async (postId, updatedPostData) => {
        try {
            await updatePost(postId, updatedPostData);
            showToast("Post updated successfully", "success");
            setShowEditPopup(false);
            setEditingPost(null);
        } catch (error) {
            console.error("Error updating post:", error);
            showToast("Failed to update post", "error");
        }
    }, [updatePost, showToast]);

    const closeEditPopup = useCallback(() => {
        setShowEditPopup(false);
        setEditingPost(null);
    }, []);

    const toggleSortDropdown = useCallback(() => {
        setShowSortDropdown(prev => !prev);
    }, []);

    return (
        <>
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={hideToast}
                />
            )}

            {showEditPopup && (
                <AddPost
                    onClose={closeEditPopup}
                    isEditing={true}
                    editingPost={editingPost}
                    onEditSave={handleEditSave}
                />
            )}

            <div className="sort-container">
                <div className="add-post-button-container">
                    <button
                        className="add-post-button"
                        onClick={handleAddPostClick}
                        disabled={community_block_post_add}
                    >
                        <FaPlus className="add-icon" />
                        <span>Create</span>
                    </button>
                </div>
                { projectInfo?.pubid !== "50134" &&
                (<div className="sort-dropdown">
                    <button
                        className="sort-button"
                        onClick={toggleSortDropdown}
                    >
                        Sorted by {sortOption === "popular" ? "Popular" : "Latest"} ▼
                    </button>
                    {showSortDropdown && (
                        <div className="sort-dropdown-content">
                           <div
                                className={`sort-option ${sortOption === "popular" ? "active" : ""}`}
                                onClick={() => handleSortSelection("popular")}
                            >
                                Popular {sortOption === "popular" && "✓"}
                            </div>
                            <div
                                className={`sort-option ${sortOption === "latest" ? "active" : ""}`}
                                onClick={() => handleSortSelection("latest")}
                            >
                                Latest {sortOption === "latest" && "✓"}
                            </div>
                        </div>
                    )}
                </div>)}
            </div>

            <div className="instagram-container">
                {((loading && !Object.keys(posts)?.length) || (!isLoaded && !Object.keys(posts)?.length)) && <SkeletonLoader count={2} />}

                {
                    postOrder.map((item) => {
                        return (
                            <PostItem
                                key={item}
                                post={posts[item]}
                                postId={item}
                                categories={categories}
                                selectedCategory={selectedCategory}
                                currentUser={currentUser}
                                activeMediaIndex={activeMediaIndices[item]}
                                onCommentDisable={onDisableComment}
                                showToast={showToast}
                                onEditPost={handleEditPost}
                                onDeletePost={handleDeletePost}
                                onCarousalClick={onCarousalClick}
                                toggleLikePost={toggleLikePost}
                                handlePollVote={handlePollVote}
                                onCommentClick={onCommentClick}
                            />
                        )
                    })
                }

                {hasNoPosts && (
                    <div className="no-posts-message">
                        <div className="no-posts-content">
                            <div className="no-posts-icon">
                                <LuFileSpreadsheet size={64} color="#A0AEC0" />
                            </div>
                            <h3>No Posts Found</h3>
                            <p>There are no posts available in this category right now.</p>
                            {selectedCategory && categories[selectedCategory] && (
                                <p>Try checking back later or explore other categories.</p>
                            )}
                        </div>
                    </div>
                )}

                {hasMore && isLoaded && <div ref={sentinelCallbackRef} style={{ height: 1 }} />}

                {loadingMore && (
                    <div className="loading-more-container">
                        <SkeletonLoader count={1} />
                    </div>
                )}
            </div>
        </>
    );
};

export default HomePage;